import { normalizeAssetListResponse } from './assetListNormalize';

export type BookingPeriodKind = 'hour' | 'day' | 'week' | 'month' | 'year';

export type AssetBookingPricingOption = {
  kind: BookingPeriodKind | null;
  pricingTypeId: number;
  label: string;
  price: number | null;
};

const BOOKING_PERIOD_ORDER: BookingPeriodKind[] = ['hour', 'day', 'week', 'month', 'year'];

const BOOKING_PERIOD_LABEL: Record<BookingPeriodKind, string> = {
  hour: 'Hour',
  day: 'Day',
  week: 'Week',
  month: 'Month',
  year: 'Year',
};

/** Fallback when catalog names are missing (common dev ids). */
const PRICING_TYPE_ID_TO_KIND: Partial<Record<number, BookingPeriodKind>> = {
  1: 'hour',
  2: 'day',
  3: 'week',
  4: 'month',
  5: 'year',
};

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function pricingTypeRowNumericId(row: Record<string, unknown>): number | null {
  const raw = row.pricing_type_id ?? row.id;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }
  if (typeof raw === 'string' && raw.trim()) {
    const n = Number(raw.trim());
    return Number.isFinite(n) ? n : null;
  }
  const nested = row.pricing_type;
  if (isPlainObject(nested)) {
    const nestedRaw = nested.pricing_type_id ?? nested.id;
    if (typeof nestedRaw === 'number' && Number.isFinite(nestedRaw)) {
      return nestedRaw;
    }
    if (typeof nestedRaw === 'string' && nestedRaw.trim()) {
      const n = Number(nestedRaw.trim());
      return Number.isFinite(n) ? n : null;
    }
  }
  return null;
}

function pricingCatalogDisplayTitle(row: Record<string, unknown>): string {
  const fromName =
    typeof row.pricing_type_name === 'string' && row.pricing_type_name.trim()
      ? row.pricing_type_name.trim()
      : typeof row.type_name === 'string' && row.type_name.trim()
        ? row.type_name.trim()
        : typeof row.name === 'string' && row.name.trim()
          ? row.name.trim()
          : '';
  return fromName;
}

/** Map API pricing type label to hour / day / week / month / year. */
export function pricingLabelToBookingPeriodKind(label: string): BookingPeriodKind | null {
  const n = label.trim().toLowerCase();
  if (!n) {
    return null;
  }
  if (/\bhour/.test(n) || n === 'hr' || n === 'hrs' || n === 'hourly') {
    return 'hour';
  }
  if (/\bday/.test(n) || n === 'daily') {
    return 'day';
  }
  if (/\bweek/.test(n) || n === 'weekly') {
    return 'week';
  }
  if (/\bmonth/.test(n) || n === 'monthly' || n === 'monthly rate') {
    return 'month';
  }
  if (/חודש/.test(label)) {
    return 'month';
  }
  if (/\byear/.test(n) || n === 'yearly' || n === 'annual') {
    return 'year';
  }
  return null;
}

function parsePrice(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    const n = Number(value.trim());
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function pricingMethodPrice(row: Record<string, unknown>): number | null {
  return (
    parsePrice(row.price) ??
    parsePrice(row.amount) ??
    parsePrice(row.rate) ??
    parsePrice(row.unit_price)
  );
}

function pricingMethodEmbeddedName(row: Record<string, unknown>): string {
  const direct =
    typeof row.pricing_type_name === 'string' && row.pricing_type_name.trim()
      ? row.pricing_type_name.trim()
      : '';
  if (direct) {
    return direct;
  }
  const nested = row.pricing_type;
  if (!isPlainObject(nested)) {
    return '';
  }
  for (const key of ['pricing_type_name', 'type_name', 'name', 'label', 'title'] as const) {
    const v = nested[key];
    if (typeof v === 'string' && v.trim()) {
      return v.trim();
    }
  }
  return '';
}

function catalogByPricingTypeId(
  catalogRows: Record<string, unknown>[],
): Map<number, Record<string, unknown>> {
  const m = new Map<number, Record<string, unknown>>();
  for (const row of catalogRows) {
    const id = pricingTypeRowNumericId(row);
    if (id !== null && !m.has(id)) {
      m.set(id, row);
    }
  }
  return m;
}

function resolveOptionLabel(kind: BookingPeriodKind | null, catalogTitle: string, pricingTypeId: number): string {
  if (catalogTitle) {
    return catalogTitle;
  }
  if (kind) {
    return BOOKING_PERIOD_LABEL[kind];
  }
  return `Pricing type ${pricingTypeId}`;
}

function compareBookingOptions(a: AssetBookingPricingOption, b: AssetBookingPricingOption): number {
  const ai = a.kind ? BOOKING_PERIOD_ORDER.indexOf(a.kind) : 99;
  const bi = b.kind ? BOOKING_PERIOD_ORDER.indexOf(b.kind) : 99;
  if (ai !== bi) {
    return ai - bi;
  }
  return a.pricingTypeId - b.pricingTypeId;
}

/**
 * Booking periods offered by an asset (`pricing_methods`), with labels from the pricing-types catalog when needed.
 */
export function resolveAssetBookingPricingOptions(
  asset: Record<string, unknown>,
  catalogRows: Record<string, unknown>[],
): AssetBookingPricingOption[] {
  const methods = asset.pricing_methods;
  if (!Array.isArray(methods) || methods.length === 0) {
    return [];
  }
  const byId = catalogByPricingTypeId(catalogRows);
  const out: AssetBookingPricingOption[] = [];
  const seen = new Set<number>();

  for (const item of methods) {
    if (!isPlainObject(item)) {
      continue;
    }
    const embeddedName = pricingMethodEmbeddedName(item);
    const kindFromName = pricingLabelToBookingPeriodKind(embeddedName);
    let pricingTypeId = pricingTypeRowNumericId(item);
    if (pricingTypeId === null && kindFromName) {
      // Search rows often send Hourly/Daily by name only — map to catalog defaults.
      const fromKind = (Object.entries(PRICING_TYPE_ID_TO_KIND) as [string, BookingPeriodKind][]).find(
        ([, kind]) => kind === kindFromName,
      );
      pricingTypeId = fromKind ? Number(fromKind[0]) : null;
    }
    if (pricingTypeId === null || seen.has(pricingTypeId)) {
      continue;
    }
    seen.add(pricingTypeId);

    const catalogRow = byId.get(pricingTypeId);
    const catalogTitle = catalogRow ? pricingCatalogDisplayTitle(catalogRow) : '';
    const nameForKind = embeddedName || catalogTitle;
    const kind = kindFromName ?? PRICING_TYPE_ID_TO_KIND[pricingTypeId] ?? null;

    out.push({
      kind,
      pricingTypeId,
      label: resolveOptionLabel(kind, nameForKind || catalogTitle, pricingTypeId),
      price: pricingMethodPrice(item),
    });
  }

  return out.sort(compareBookingOptions);
}

export function assetSupportsHourlyBooking(options: AssetBookingPricingOption[]): boolean {
  return options.some((o) => o.kind === 'hour');
}

/** Normalize pricing-types API payload to catalog rows. */
export function normalizePricingTypesCatalog(raw: unknown): Record<string, unknown>[] {
  return normalizeAssetListResponse(raw);
}

export function formatBookingOptionPrice(price: number | null): string {
  if (price === null) {
    return '';
  }
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(price);
}

/** Consumer listing: hourly (id 1), daily (id 2), and monthly (id 4) from `pricing_methods`. */
export function resolveListingHourlyDailyPrices(asset: Record<string, unknown>): {
  hourly: number | null;
  daily: number | null;
  monthly: number | null;
} {
  const methods = asset.pricing_methods;
  let hourly: number | null = null;
  let daily: number | null = null;
  let monthly: number | null = null;
  if (!Array.isArray(methods)) {
    return { hourly, daily, monthly };
  }
  for (const item of methods) {
    if (!isPlainObject(item)) {
      continue;
    }
    const pricingTypeId = pricingTypeRowNumericId(item);
    const embeddedName = pricingMethodEmbeddedName(item);
    const kindFromName = pricingLabelToBookingPeriodKind(embeddedName);
    const kind =
      kindFromName ??
      (pricingTypeId !== null ? PRICING_TYPE_ID_TO_KIND[pricingTypeId] ?? null : null);
    const price = pricingMethodPrice(item);
    if (kind === 'hour' && hourly === null) {
      hourly = price;
    }
    if (kind === 'day' && daily === null) {
      daily = price;
    }
    if (kind === 'month' && monthly === null) {
      monthly = price;
    }
  }
  return { hourly, daily, monthly };
}
